import { useEffect, useState } from 'react';
import axios from 'axios';
import { Wand2 } from 'lucide-react';
import useFuelBrands from '../hooks/useFuelBrands.js';
import usePosTypes from '../hooks/usePosTypes.js';
import DateInput from './DateInput.jsx';
import NewTicketModal from './NewTicketModal.jsx';
import { useAuth } from '../context/AuthContext.jsx';

// Statuses are a fixed taxonomy. Fuel brands and POS types come from
// user-managed tables via their hooks so the Settings page can
// add/edit/delete them live.
const STATUSES    = ['Active', 'Inactive', 'Pending'];
// xsiteio onboarding workflow states -- milestone-based, 6 stages
// (see migrate-sites-onboarding-status-v2). Sites default to
// 'Not started' on creation and walk forward as milestones are hit.
// Legacy 'In progress' rows were remapped to 'Client added' by the v2
// migration -- no code writes 'In progress' anymore.
const ONBOARDING_STATUSES = [
  'Not started',
  'Client added',
  'Site configured',
  'Bulloch config done & tested',
  'BT900 Price Book imported',
  'Fuel Manager setup complete',
  'Site Ready email sent',
  'Training scheduled',
  'Training complete',
  'Done',
];

// Build the initial form state. For edit mode we accept an existing site
// row plus the latest configuration for that site (joined client-side
// from the customer-detail payload). Credentials live on their own
// table now and are managed via CredentialForm, so the site form is
// Helper — server returns DATE values as ISO strings with time. The
// HTML date input wants YYYY-MM-DD, so trim. Empty / invalid stays ''.
function isoDate(value) {
  if (!value) return '';
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

// Form initial state — site fields + the PRIMARY SFTP credential.
// Multiple credential sets (Back Office, PetroCan, …) still live on
// site_credentials and are managed via the + Credential button on the
// site row. The inline fields here cover the first/primary set; for a
// brand-new site they're empty, and saving with values populates a new
// credential row.
function makeInitial(site, primaryCredential) {
  const cred = primaryCredential || {};
  return {
    site_number:       site?.site_number    || '',
    site_name:         site?.site_name      || '',
    address:           site?.address        || '',
    city:              site?.city           || '',
    province_state:    site?.province_state || '',
    postal_zip:        site?.postal_zip     || '',
    fuel_brand:        site?.fuel_brand     || '',
    pos_type:          site?.pos_type       || '',
    status:            site?.status         || 'Active',
    onboarding_status: site?.onboarding_status || 'Not started',
    install_date:      isoDate(site?.install_date),
    renewal_date:      isoDate(site?.renewal_date),
    status_changed_at: isoDate(site?.status_changed_at),
    xsite_io_username: site?.xsite_io_username || '',
    xsite_io_password: site?.xsite_io_password || '',
    site_phone:        site?.site_phone        || '',
    site_manager:      site?.site_manager      || '',
    site_email:        site?.site_email        || '',
    sftp_host:         cred.host            || '',
    sftp_port:         cred.port != null ? String(cred.port) : '',
    sftp_username:     cred.username        || '',
    sftp_password:     cred.password        || '',
    date_received:     isoDate(cred.date_received),
    // xsiteio SFTP file-transfer config (Step 11/12). Defaults match
    // the values xsiteio pre-fills on its SFTP screen so Mary doesn't
    // have to retype the standard config for every new site.
    sftp_source_dir:       site?.sftp_source_dir       || '/receive',
    sftp_destination_dir:  site?.sftp_destination_dir  || '/send/priceb',
    sftp_run_time_minutes: site?.sftp_run_time_minutes != null ? String(site.sftp_run_time_minutes) : '5',
    sftp_file_types:       site?.sftp_file_types       || 'trs,trx,sft'
  };
}

// `customerId` is required (POST needs it). `site` is passed only for
// Edit mode; when omitted, the form is in Add mode. `primaryCredential`
// is the lowest-id site_credentials row for this site (the "primary"
// credential), used to pre-fill the inline SFTP fields. `parentRecord`
// is the customer (or lead) row this site belongs to — surfaces the
// "Auto-populate from Record" button in Add mode.
export default function SiteForm({ open, customerId, site, primaryCredential, parentRecord, onClose, onSaved }) {
  const isEdit = !!site;
  const { user } = useAuth();
  const [form, setForm] = useState(() => makeInitial(site, primaryCredential));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [ticketOpen, setTicketOpen] = useState(false);
  // Pull SFTP from xsiteio button state (Dave 2026-06-12). Surfaces
  // here so a site with no local credential can be backfilled in one
  // click from xsiteio without the user having to save first to
  // create a credential row, then re-open the credential editor.
  const [pullingSftp, setPullingSftp] = useState(false);
  const [sftpPullResult, setSftpPullResult] = useState(null); // { tone, message } | null
  const fuelBrands = useFuelBrands();
  const posTypes = usePosTypes();

  useEffect(() => {
    if (!open) return;
    setForm(makeInitial(site, primaryCredential));
    setError(null);
  }, [open, site, primaryCredential]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, saving]);

  if (!open) return null;

  function update(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  // Pull the live SFTP config from xsiteio and fold the result back
  // into the form fields so the user sees the pulled values before
  // saving. Only available in Edit mode (need a site_id). The server
  // writes to site_credentials + sites directly, so a successful pull
  // also closes the modal -- the parent re-fetches and the new values
  // are visible immediately.
  async function handlePullSftpFromXsiteio() {
    if (!isEdit || !site?.id) return;
    setPullingSftp(true);
    setSftpPullResult(null);
    try {
      const res = await axios.post(`/api/sites/${site.id}/xsiteio/pull-sftp`, {});
      const c = res.data?.changed || {};
      const cred = c.credential || {};
      // Reflect the pulled values into the form state so they're
      // visible immediately (the user doesn't have to close + reopen
      // the modal to see what came back).
      setForm((prev) => ({
        ...prev,
        sftp_host:        cred.host        ?? prev.sftp_host,
        sftp_port:        cred.port != null ? String(cred.port) : prev.sftp_port,
        sftp_username:    cred.username    ?? prev.sftp_username,
        // Pull returns the actual password verbatim (same as xsiteio's
        // own SFTP details form -- admin/staff only, HTTPS in transit).
        sftp_password:    cred.password ?? prev.sftp_password,
        // Pull stamps date_received = today so the form reflects the
        // last time SFTP details were re-confirmed against xsiteio.
        date_received:    cred.date_received ?? prev.date_received,
        sftp_source_dir:      c.site?.sftp_source_dir       ?? prev.sftp_source_dir,
        sftp_destination_dir: c.site?.sftp_destination_dir  ?? prev.sftp_destination_dir,
        sftp_run_time_minutes: c.site?.sftp_run_time_minutes != null
                            ? String(c.site.sftp_run_time_minutes) : prev.sftp_run_time_minutes,
        sftp_file_types:      c.site?.sftp_file_types       ?? prev.sftp_file_types
      }));
      const parts = [];
      if (c.credential) parts.push('credential');
      if (c.site)       parts.push(Object.keys(c.site).map((k) => k.replace(/^sftp_/, '')).join(' + '));
      setSftpPullResult({
        tone: 'good',
        message: parts.length
          ? `Pulled: ${parts.join(' / ')} — review then Save Changes`
          : 'Pulled (no changes -- already in sync).'
      });
    } catch (err) {
      // Surface the upstream xsiteio status + body so we can debug
      // which endpoint shape it expects (Dave 2026-06-12 -- still
      // converging on the right read path).
      const d = err.response?.data || {};
      const parts = [d.error || err.message];
      if (d.status) parts.push(`status ${d.status}`);
      if (d.body)   parts.push(typeof d.body === 'string' ? d.body : JSON.stringify(d.body).slice(0, 200));
      setSftpPullResult({
        tone: 'bad',
        message: parts.join(' — ')
      });
    } finally {
      setPullingSftp(false);
    }
  }
  const canPullSftp = isEdit && !!site?.id;

  // Copy 8 fields from the parent customer/lead record into the form
  // state. Only fills fields where the parent has a value — empty
  // parent fields don't clobber anything the user may have typed.
  // Available only in Add mode (the bar is hidden in Edit mode).
  function autoPopulateFromParent() {
    if (!parentRecord) return;
    setForm((prev) => {
      const copy = { ...prev };
      const apply = (formKey, parentValue) => {
        if (parentValue != null && parentValue !== '') copy[formKey] = parentValue;
      };
      apply('site_number',    parentRecord.site_number);
      apply('site_name',      parentRecord.company);             // company → site name
      apply('address',        parentRecord.address1);
      apply('city',           parentRecord.city);
      apply('province_state', parentRecord.province_state);
      apply('postal_zip',     parentRecord.postal_zip);
      apply('fuel_brand',     parentRecord.fuel_brand);
      apply('pos_type',       parentRecord.pos_type);
      apply('site_phone',     parentRecord.phone_main);
      const fullName = [parentRecord.first_name, parentRecord.last_name].filter(Boolean).join(' ');
      apply('site_manager',   fullName || null);
      return copy;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form };
      if (!isEdit) payload.customer_id = customerId;

      const res = isEdit
        ? await axios.put(`/api/sites/${site.id}`, payload)
        : await axios.post('/api/sites', payload);

      if (onSaved) onSaved(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={(e) => {
      if (e.target === e.currentTarget && !saving) onClose();
    }}>
      <div className="modal-card" role="dialog" aria-modal="true" aria-label={isEdit ? 'Edit Site' : 'Add Site'}>
        <header className="modal-head">
          <h3>{isEdit ? `Edit Site${site?.site_number ? ` · ${site.site_number}` : ''}` : 'Add Site'}</h3>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >×</button>
        </header>

        <form className="modal-body" onSubmit={handleSubmit}>
          {/* Auto-populate from parent record — Add mode only. Pulls
              the 8 fields most customers want copied straight from the
              customer/lead record. User can still edit any value
              before saving. Hidden in Edit mode (no point re-copying
              when the user is editing the site's own data). */}
          {!isEdit && parentRecord && (
            <div className="auto-populate-bar">
              <button
                type="button"
                className="btn-secondary btn-sm auto-populate-btn"
                onClick={autoPopulateFromParent}
              >
                <Wand2 size={14} strokeWidth={1.75} />
                Auto-populate from Record
              </button>
              <span className="auto-populate-hint">
                Copies Site Number, Company → Site Name, Address, City,
                Province, Postal, Fuel Brand, POS Type, Phone, Manager
                {parentRecord.company && <> from <strong>{parentRecord.company}</strong></>}.
                Edit any field before saving.
              </span>
            </div>
          )}

          <div className="form-grid">
            <label className="form-field">
              <span>Site Number</span>
              <input type="text" value={form.site_number} onChange={update('site_number')} className="cell-mono" />
            </label>
            <label className="form-field">
              <span>Site Name</span>
              <input type="text" value={form.site_name} onChange={update('site_name')} />
            </label>
            <label className="form-field form-field-wide">
              <span>Address</span>
              <input type="text" value={form.address} onChange={update('address')} />
            </label>
            <label className="form-field">
              <span>City</span>
              <input type="text" value={form.city} onChange={update('city')} />
            </label>
            <label className="form-field">
              <span>Province / State</span>
              <input type="text" value={form.province_state} onChange={update('province_state')} />
            </label>
            <label className="form-field">
              <span>Postal / Zip</span>
              <input type="text" value={form.postal_zip} onChange={update('postal_zip')} className="cell-mono" />
            </label>
            <label className="form-field">
              <span>Fuel Brand</span>
              <select value={form.fuel_brand} onChange={update('fuel_brand')}>
                <option value="">—</option>
                {fuelBrands.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </label>
            <label className="form-field">
              <span>POS Type</span>
              <select value={form.pos_type} onChange={update('pos_type')}>
                <option value="">—</option>
                {posTypes.filter((t) => t.active).map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
                {/* Keep a legacy/removed value showable so editing a record
                    doesn't silently blank its POS type. */}
                {form.pos_type && !posTypes.some((t) => t.name === form.pos_type) && (
                  <option value={form.pos_type}>{form.pos_type}</option>
                )}
              </select>
            </label>
            <label className="form-field">
              <span>Status</span>
              <select value={form.status} onChange={update('status')}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="form-field">
              {/* xsiteio onboarding workflow state. New sites default to
                  'Not started' and appear in the Onboarding Queue.
                  Milestone-based progression (10 stages):
                    Not started -> Client added -> Site configured ->
                    Bulloch config done & tested -> BT900 Price Book
                    imported -> Fuel Manager setup complete ->
                    Site Ready email sent -> Training scheduled ->
                    Training complete -> Done
                  Sites stay on the Onboarding Queue until they hit Done.
                  Flip back to 'Not started' any time to re-queue.
                  Passport (US) sites can skip 'Bulloch config done &
                  tested' since they don't use SFTP the same way.
                  'Fuel Manager setup complete' is skippable for sites
                  that don't subscribe to Fuel Manager (jump straight
                  to Site Ready email sent). */}
              <span>Onboarding</span>
              <select value={form.onboarding_status} onChange={update('onboarding_status')}>
                {ONBOARDING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            {/* Date Deactivated -- only visible for Inactive sites.
                Auto-populated when Dave flips a site from Active to
                Inactive via the normal status change (sitesController
                stamps NOW() on the change). For historical sites where
                we don't know when they actually went inactive, the
                field is editable so Dave can backfill -- the Finance
                "Lost ARR (last 12 months)" metric uses these dates. */}
            {form.status === 'Inactive' && (
              <label className="form-field">
                <span>Date Deactivated</span>
                <DateInput
                  value={form.status_changed_at}
                  onChange={update('status_changed_at')}
                  className="cell-mono"
                />
              </label>
            )}
            <label className="form-field">
              <span>Site Phone</span>
              <input type="text" value={form.site_phone} onChange={update('site_phone')} className="cell-mono" />
            </label>
            <label className="form-field">
              <span>Site Manager</span>
              <input type="text" value={form.site_manager} onChange={update('site_manager')} />
            </label>
            <label className="form-field">
              <span>Site Email</span>
              {/* Customer-editable from the portal (Akbar can change it) --
                  shown here so admins always see the current value. Portal
                  edits also log a note on the contact with before/after. */}
              <input type="email" value={form.site_email} onChange={update('site_email')} className="cell-mono" />
            </label>
            {/* "Changed via portal" stamp -- set server-side whenever the
                customer edits phone/manager/email from the portal. Tells
                an admin at a glance that these contact values are
                customer-supplied. Full change history is in the notes. */}
            {site?.portal_updated_at && (
              <p className="portal-stamp">
                ⚑ Contact info updated via customer portal by{' '}
                <strong>{site.portal_updated_by || 'customer'}</strong>{' '}
                on {String(site.portal_updated_at).slice(0, 10)}
              </p>
            )}
            <label className="form-field">
              <span>Install Date</span>
              <DateInput value={form.install_date} onChange={update('install_date')} className="cell-mono" />
            </label>
            <label className="form-field">
              <span>Renewal Date</span>
              <DateInput value={form.renewal_date} onChange={update('renewal_date')} className="cell-mono" />
            </label>
            <label className="form-field">
              <span>
                XSite IO Username
                {/* Onboarding convention (Dave 2026-06-04): both
                    username and password are identical, formatted as
                    {province}-{site_name no spaces}{site_number}@xsite.com.
                    Button fills both fields at once; user can edit
                    after. Hidden when prov/name+num aren't filled in
                    yet (nothing to build from). */}
                {(() => {
                  // Format (Dave 2026-06-04 final): pull first word
                  // of site_name, insert digits of site_number (leading
                  // zeros stripped), then append the rest of the
                  // site_name. e.g. 'Centex Outlook' / 'cen00123' / 'SK'
                  // -> 'sk-centex123outlook@xsite.com'.
                  const prov = String(form.province_state || '').toLowerCase().trim();
                  const nameRaw = String(form.site_name || '').toLowerCase().trim();
                  const words = nameRaw.split(/\s+/).filter(Boolean);
                  const firstWord = words[0] || '';
                  const restName  = words.slice(1).join('');
                  const digitsRaw = (String(form.site_number || '').match(/\d+/g) || []).join('');
                  const numPart   = digitsRaw ? String(parseInt(digitsRaw, 10)) : '';
                  const cred = (prov && firstWord)
                    ? `${prov}-${firstWord}${numPart}${restName}@xsite.com`
                    : null;
                  if (!cred) return null;
                  return (
                    <button
                      type="button"
                      onClick={() => {
                        setForm((f) => ({
                          ...f,
                          xsite_io_username: cred,
                          xsite_io_password: cred
                        }));
                      }}
                      title={`Fill both with ${cred}`}
                      style={{
                        marginLeft: 'var(--space-2)',
                        padding: '2px 8px',
                        fontSize: 'var(--text-caption)',
                        background: 'var(--color-accent-soft)',
                        color: 'var(--color-accent-dark)',
                        border: '1px solid var(--color-accent)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        fontWeight: 'var(--font-semibold)'
                      }}
                    >
                      Generate
                    </button>
                  );
                })()}
              </span>
              <input
                type="text"
                value={form.xsite_io_username}
                onChange={update('xsite_io_username')}
                className="cell-mono"
                autoComplete="off"
              />
            </label>
            <label className="form-field">
              <span>XSite IO Password</span>
              <input
                type="text"
                value={form.xsite_io_password}
                onChange={update('xsite_io_password')}
                className="cell-mono"
                autoComplete="off"
              />
            </label>
          </div>

          {/* Raise a support ticket for this site. Edit mode only —
              a brand-new unsaved site has no id/name to attach to. */}
          {isEdit && (
            <div className="site-ticket-row">
              <button type="button" className="btn-secondary btn-sm" onClick={() => setTicketOpen(true)}>
                + Ticket
              </button>
              <span className="form-hint">Open a support ticket pre-filled with this site.</span>
            </div>
          )}

          <h4 className="preview-heading" style={{ marginTop: 'var(--space-5)' }}>
            SFTP Credentials
          </h4>
          <p className="modal-sub" style={{ marginTop: 0 }}>
            Primary SFTP login for this site. Need a second set (e.g.
            Back Office vs PetroCan)? Save first, then use the
            {' '}<strong>+ Credential</strong> button on the site row to
            add more.
          </p>
          {canPullSftp && (
            <div style={{
              marginTop: 'var(--space-2)',
              marginBottom: 'var(--space-3)',
              padding: 'var(--space-3)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-surface-sunken)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              flexWrap: 'wrap'
            }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={handlePullSftpFromXsiteio}
                disabled={pullingSftp || saving}
              >
                {pullingSftp ? 'Pulling…' : 'Pull SFTP from xsiteio'}
              </button>
              <span className="form-hint" style={{ flex: 1 }}>
                Reads the live host / port / username / password + source/dest dirs
                + file types + run time from xsiteio's "Client's SFTP details"
                form and writes them into Command. Use this to backfill sites
                configured directly on xsiteio's side.
              </span>
              {sftpPullResult && (
                <span
                  className="form-hint"
                  style={{
                    width: '100%',
                    color: sftpPullResult.tone === 'bad'  ? 'var(--color-bad)'
                         : sftpPullResult.tone === 'good' ? 'var(--color-good)'
                         : 'var(--color-text-dim)'
                  }}
                >
                  {sftpPullResult.message}
                </span>
              )}
            </div>
          )}
          <div className="form-grid">
            <label className="form-field">
              <span>Host</span>
              <input type="text" value={form.sftp_host} onChange={update('sftp_host')} className="cell-mono" autoComplete="off" />
            </label>
            <label className="form-field">
              <span>Port</span>
              <input type="number" value={form.sftp_port} onChange={update('sftp_port')} className="cell-mono" />
            </label>
            <label className="form-field">
              <span>Username</span>
              <input type="text" value={form.sftp_username} onChange={update('sftp_username')} className="cell-mono" autoComplete="off" />
            </label>
            <label className="form-field">
              <span>Password</span>
              <input type="text" value={form.sftp_password} onChange={update('sftp_password')} className="cell-mono" autoComplete="off" />
            </label>
            <label className="form-field">
              <span>Date Received</span>
              <DateInput value={form.date_received} onChange={update('date_received')} className="cell-mono" />
            </label>
          </div>

          {/* SFTP Configuration — what gets transferred + cadence. This
              is xsiteio Step 11/12 territory (push endpoint not yet
              documented by Jose, but storing the values locally so
              Command captures what was set per-site). Defaults match
              the xsiteio defaults from the screen Dave screenshot 2026-06-07. */}
          <h4 className="preview-heading" style={{ marginTop: 'var(--space-5)' }}>
            SFTP Configuration
          </h4>
          <p className="modal-sub" style={{ marginTop: 0 }}>
            Source/destination directories, polling cadence, and the
            file-type whitelist. Defaults to xsiteio's standard config —
            change only when a site needs a custom setup.
          </p>
          <div className="form-grid">
            <label className="form-field">
              <span>Source Directory</span>
              <input type="text" value={form.sftp_source_dir} onChange={update('sftp_source_dir')} className="cell-mono" placeholder="/receive" />
            </label>
            <label className="form-field">
              <span>Destination Directory</span>
              <input type="text" value={form.sftp_destination_dir} onChange={update('sftp_destination_dir')} className="cell-mono" placeholder="/send/priceb" />
            </label>
            <label className="form-field">
              <span>SFTP Run Time</span>
              <select value={form.sftp_run_time_minutes} onChange={update('sftp_run_time_minutes')}>
                <option value="1">1 Minute</option>
                <option value="5">5 Minutes</option>
                <option value="15">15 Minutes</option>
                <option value="30">30 Minutes</option>
                <option value="60">60 Minutes</option>
              </select>
            </label>
            <label className="form-field">
              <span>File Type Support</span>
              <input type="text" value={form.sftp_file_types} onChange={update('sftp_file_types')} className="cell-mono" placeholder="trs,trx,sft" />
            </label>
          </div>

          {error && <p className="error" style={{ marginTop: 'var(--space-3)' }}>{error}</p>}

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Site')}
            </button>
          </div>
        </form>
      </div>

      {isEdit && (
        <NewTicketModal
          open={ticketOpen}
          preselectedSiteId={site.id}
          currentUserId={user?.id}
          onCancel={() => setTicketOpen(false)}
          onCreated={() => setTicketOpen(false)}
        />
      )}
    </div>
  );
}
